import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

// Subset of the raw BatchSessions TPC row returned by
// list_parent_connect_for_profile. The calendar needs the timing +
// status to render a row, `last_rescheduled_role` to tell a parent-side
// request apart from a trainer-rescheduled one (drives the pill), and
// `variant_id` (stamped server-side from EnrolledProducts) to deep-link
// the "Request pending" pill to the respond page.
export interface ParentConnectSessionRow {
  session_id: string;
  start_time: number;
  end_time?: number;
  session_status?: string;
  // 'trainer' once the trainer has suggested a new time (parent must
  // respond); 'parent' / absent otherwise.
  last_rescheduled_role?: string;
  variant_id?: string;
  batch_id?: string;
  parent_connect_title?: string;
  parent_connect_reason?: string;
  // 'trainer' when the trainer dashboard booked this session
  // (create-teacher-parent-connect); 'parent' / absent otherwise. Only
  // trainer-initiated ACTIVE rows get the drawer "Cancel / Reschedule"
  // affordance (see utils/map-parent-connect.ts).
  initiated_by?: string;
  // Parent-side counter-reschedule count (cap 2, enforced by BSS). Used
  // for parent-initiated rows to show "Reschedule attempts left: N" in
  // the drawer. Absent on legacy rows -> treated as 0.
  parent_reschedule_request_count?: number;
}

// service-user wraps every parent-connect response in the standard
// `{ status, responseBody, ... }` envelope; the axios response
// interceptor unwraps the outer HTTP response, so we type the inner
// envelope here (same pattern as parent-connect-booking/get-overview).
type ServiceUserEnvelope<T> = {
  message?: string;
  status?: string;
  responseBody?: T;
  errorCode?: number;
  errorMessage?: string | null;
};

// "Not inactive" set: every status a parent-connect row can hold except
// `inactive` (pending request that was cancelled/rejected/expired before
// it ever became a booked session).
const PARENT_CONNECT_STATUSES = 'pending,active,completed,cancelled,deleted';

// `responseBody.requests` is normally the array, but tolerate a
// `{ requests: [...] }` nested wrapper too so the client keeps working
// regardless of which side (service-user) unwraps the BSS envelope.
const extractRequests = (
  body:
    | ParentConnectSessionRow[]
    | { requests?: ParentConnectSessionRow[] }
    | undefined
): ParentConnectSessionRow[] => {
  if (Array.isArray(body)) {
    return body;
  }
  if (body && Array.isArray(body.requests)) {
    return body.requests;
  }
  return [];
};

export const getParentConnectSessions = async (): Promise<
  ParentConnectSessionRow[]
> => {
  const data: ServiceUserEnvelope<
    ParentConnectSessionRow[] | { requests?: ParentConnectSessionRow[] }
  > = await axios.get('/user/v1/trainer-slots/parent-connect/requests', {
    params: { status: PARENT_CONNECT_STATUSES },
  });
  return extractRequests(data?.responseBody);
};

// Dev-only seed rows so the drawer's PC states are all exercisable
// locally (the real list comes from list_parent_connect_for_profile).
// Covers: a parent-initiated request the trainer hasn't touched (shows
// the direct "Reschedule" box + "attempts left: 2"), the same after the
// parent has used up both reschedules (no box, "attempts left: 0"), a
// trainer-rescheduled row (clickable "Request pending" -> respond page),
// and a trainer-initiated active row (shows "Cancel / Reschedule").
const buildMockParentConnectRows = (): ParentConnectSessionRow[] => {
  const nowEpoch = Math.floor(Date.now() / 1000);
  const hours = (n: number) => nowEpoch + n * 60 * 60;
  return [
    {
      session_id: 'mock-pc-parent-fresh',
      start_time: hours(36),
      end_time: hours(36) + 1800,
      session_status: 'pending',
      initiated_by: 'parent',
      parent_reschedule_request_count: 0,
      variant_id: 'mock-variant-id',
      batch_id: 'mock-batch-id',
    },
    {
      session_id: 'mock-pc-parent-exhausted',
      start_time: hours(40),
      end_time: hours(40) + 1800,
      session_status: 'pending',
      initiated_by: 'parent',
      parent_reschedule_request_count: 2,
      variant_id: 'mock-variant-id',
      batch_id: 'mock-batch-id',
    },
    {
      session_id: 'mock-pc-trainer-rescheduled',
      start_time: hours(44),
      end_time: hours(44) + 1800,
      session_status: 'pending',
      initiated_by: 'parent',
      last_rescheduled_role: 'trainer',
      parent_reschedule_request_count: 1,
      variant_id: 'mock-variant-id',
      batch_id: 'mock-batch-id',
    },
    {
      session_id: 'mock-pc-trainer-active',
      start_time: hours(48),
      end_time: hours(48) + 1800,
      session_status: 'active',
      initiated_by: 'trainer',
      variant_id: 'mock-variant-id',
      batch_id: 'mock-batch-id',
    },
  ];
};

export const getParentConnectSessionsMockHandler = http.get(
  `${env.API_URL}/user/v1/trainer-slots/parent-connect/requests`,
  async () => {
    await networkDelay();
    return HttpResponse.json<
      ServiceUserEnvelope<{ requests: ParentConnectSessionRow[] }>
    >({
      status: 'SUCCESS',
      message: 'Parent-connect requests fetched',
      errorCode: 0,
      errorMessage: null,
      responseBody: { requests: buildMockParentConnectRows() },
    });
  }
);
